import React, { useState, useEffect, useRef } from 'react';
import { ShoppingBag, Plus, Minus, ShoppingCart, X, Loader2, Package, CheckCircle, Trash2, Edit, Tag, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  apparel: { label: 'Apparel', icon: '👕' },
  accessories: { label: 'Accessories', icon: '🎒' },
  drinkware: { label: 'Drinkware', icon: '☕' },
  stickers: { label: 'Stickers & Decals', icon: '🏷️' },
  other: { label: 'Other', icon: '📦' },
};

export default function MerchStore({ isAdmin = false }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [tab, setTab] = useState('shop');
  const [editProduct, setEditProduct] = useState(null);
  const [adminOrders, setAdminOrders] = useState([]);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Check for order success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('order') === 'success') {
      toast.success(`Order ${params.get('id') || ''} placed successfully!`);
      setCart([]);
      setTab('orders');
    }
  }, []);

  const fetchProducts = async () => {
    try {
      const endpoint = isAdmin ? '/api/admin/merchandise' : '/api/merchandise';
      const res = await axios.get(`${API}${endpoint}`, { headers });
      setProducts(res.data || []);
    } catch { /* empty */ } finally { setLoading(false); }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/api/merchandise/orders`, { headers });
      setOrders(res.data || []);
    } catch { /* empty */ }
  };

  const fetchAdminOrders = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/api/admin/merchandise/orders`, { headers });
      setAdminOrders(res.data || []);
    } catch { /* empty */ }
  };

  useEffect(() => { fetchProducts(); fetchOrders(); if (isAdmin) fetchAdminOrders(); }, []);

  const addToCart = (product, size = '') => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id && i.size === size);
      if (existing) return prev.map(i => i.product_id === product.id && i.size === size ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { product_id: product.id, name: product.name, price: product.price, size, quantity: 1, image: product.image }];
    });
    toast.success(`${product.name} added to cart`);
  };

  const updateCartQty = (idx, delta) => {
    setCart(prev => prev.map((item, i) => i === idx ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item).filter(i => i.quantity > 0));
  };

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const handleCheckout = async () => {
    if (cart.length === 0) return toast.error('Cart is empty');
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API}/api/merchandise/checkout`, {
        items: cart.map(i => ({ product_id: i.product_id, size: i.size, quantity: i.quantity }))
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  // Admin: save product
  const saveProduct = async () => {
    if (!editProduct) return;
    try {
      if (editProduct.id) {
        await axios.put(`${API}/api/admin/merchandise/${editProduct.id}`, editProduct, { headers });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/api/admin/merchandise`, editProduct, { headers });
        toast.success('Product created');
      }
      setEditProduct(null);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/api/admin/merchandise/${id}`, { headers });
    toast.success('Product deleted');
    fetchProducts();
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/api/admin/merchandise/orders/${orderId}/status`, { status }, { headers });
      toast.success(`Order updated to ${status}`);
      fetchAdminOrders();
    } catch { toast.error('Failed to update order'); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <ShoppingBag className="h-6 w-6 text-amber-600" />
          Thrive Merch Store
        </h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button onClick={() => setEditProduct({ name: '', description: '', price: 0, image: '', category: 'apparel', sizes: [], active: true, sort_order: 0 })} variant="outline" size="sm" data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Product
            </Button>
          )}
          <Button onClick={() => setShowCart(!showCart)} variant="outline" size="sm" className="relative" data-testid="cart-btn">
            <ShoppingCart className="h-4 w-4 mr-1" /> Cart
            {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 rounded-lg p-1">
        <button onClick={() => setTab('shop')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'shop' ? 'bg-white shadow text-amber-700' : 'text-stone-600'}`}>Shop</button>
        <button onClick={() => { setTab('orders'); fetchOrders(); }} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'orders' ? 'bg-white shadow text-amber-700' : 'text-stone-600'}`}>My Orders</button>
        {isAdmin && <button onClick={() => { setTab('admin-orders'); fetchAdminOrders(); }} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${tab === 'admin-orders' ? 'bg-white shadow text-amber-700' : 'text-stone-600'}`}>All Orders</button>}
      </div>

      {/* Cart Drawer */}
      {showCart && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-stone-800">Your Cart ({cartCount})</h3>
              <button onClick={() => setShowCart(false)}><X className="h-4 w-4" /></button>
            </div>
            {cart.length === 0 ? (
              <p className="text-sm text-stone-500">Your cart is empty</p>
            ) : (
              <>
                {cart.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-amber-200 last:border-0">
                    <div>
                      <span className="text-sm font-medium text-stone-800">{item.name}</span>
                      {item.size && <span className="text-xs text-stone-500 ml-2">({item.size})</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateCartQty(idx, -1)} className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                      <span className="text-sm font-bold w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateCartQty(idx, 1)} className="w-6 h-6 rounded-full bg-amber-200 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                      <span className="text-sm font-bold text-stone-800 w-16 text-right">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-300">
                  <span className="font-bold text-stone-800">Total</span>
                  <span className="text-lg font-bold text-amber-700">${cartTotal.toFixed(2)}</span>
                </div>
                <Button onClick={handleCheckout} disabled={checkingOut} className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white" data-testid="checkout-btn">
                  {checkingOut ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
                  Checkout with Stripe
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Shop Tab */}
      {tab === 'shop' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.length === 0 ? (
            <div className="col-span-full text-center py-12 text-stone-500">
              <ShoppingBag className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="font-medium">No merchandise available yet</p>
              {isAdmin && <p className="text-sm mt-1">Click "Add Product" to create your first item</p>}
            </div>
          ) : products.map(p => (
            <ProductCard key={p.id} product={p} onAddToCart={addToCart} isAdmin={isAdmin} onEdit={() => setEditProduct({ ...p })} onDelete={() => deleteProduct(p.id)} />
          ))}
        </div>
      )}

      {/* My Orders Tab */}
      {tab === 'orders' && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Package className="h-12 w-12 mx-auto text-stone-300 mb-3" />
              <p className="font-medium">No orders yet</p>
            </div>
          ) : orders.map(o => (
            <OrderCard key={o.id} order={o} />
          ))}
        </div>
      )}

      {/* Admin Orders Tab */}
      {tab === 'admin-orders' && isAdmin && (
        <div className="space-y-3">
          {adminOrders.length === 0 ? (
            <p className="text-center py-8 text-stone-500">No orders yet</p>
          ) : adminOrders.map(o => (
            <OrderCard key={o.id} order={o} isAdmin onStatusChange={updateOrderStatus} />
          ))}
        </div>
      )}

      {/* Edit Product Modal */}
      {editProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditProduct(null)}>
          <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-bold">{editProduct.id ? 'Edit Product' : 'New Product'}</h3>
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input value={editProduct.name} onChange={e => setEditProduct({ ...editProduct, name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input value={editProduct.description} onChange={e => setEditProduct({ ...editProduct, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Price ($)</label>
                  <Input type="number" value={editProduct.price} onChange={e => setEditProduct({ ...editProduct, price: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="text-sm font-medium">Category</label>
                  <select value={editProduct.category} onChange={e => setEditProduct({ ...editProduct, category: e.target.value })} className="w-full border border-stone-300 rounded-lg h-10 px-3 text-sm">
                    {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Product Photos (up to 3)</label>
                {editProduct.id ? (
                  <ProductPhotoUploader productId={editProduct.id} images={editProduct.images || []} headers={headers} onUpdate={(imgs) => { setEditProduct({ ...editProduct, images: imgs, image: imgs[0] || '' }); fetchProducts(); }} />
                ) : (
                  <p className="text-xs text-stone-500 mt-1">Save the product first, then upload photos</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Sizes (comma-separated, leave empty for one-size)</label>
                <Input value={(editProduct.sizes || []).join(', ')} onChange={e => setEditProduct({ ...editProduct, sizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="S, M, L, XL" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editProduct.active !== false} onChange={e => setEditProduct({ ...editProduct, active: e.target.checked })} />
                <label className="text-sm">Active (visible in store)</label>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => setEditProduct(null)} variant="outline" className="flex-1">Cancel</Button>
                <Button onClick={saveProduct} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">Save Product</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onAddToCart, isAdmin, onEdit, onDelete }) {
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || '');
  const images = product.images?.length > 0 ? product.images : (product.image ? [product.image] : []);
  const [imgIdx, setImgIdx] = useState(0);
  const cat = CATEGORIES[product.category] || CATEGORIES.other;
  return (
    <Card className="overflow-hidden border-stone-200 hover:border-amber-300 hover:shadow-md transition-all group">
      {images.length > 0 ? (
        <div className="aspect-square bg-stone-100 overflow-hidden relative">
          <img src={images[imgIdx] || images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center transition-colors"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={(e) => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }} className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-7 h-7 flex items-center justify-center transition-colors"><ChevronRight className="h-4 w-4" /></button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === imgIdx ? 'bg-white' : 'bg-white/50'}`} />)}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="aspect-square bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
          <ShoppingBag className="h-16 w-16 text-amber-300" />
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-1">
          <div>
            <span className="text-xs text-amber-600 font-medium">{cat.icon} {cat.label}</span>
            <h3 className="font-bold text-stone-800 text-lg leading-tight">{product.name}</h3>
          </div>
          <span className="text-xl font-bold text-amber-700">${product.price.toFixed(2)}</span>
        </div>
        {product.description && <p className="text-sm text-stone-500 mt-1 line-clamp-2">{product.description}</p>}
        {!product.active && <span className="text-xs text-red-600 font-bold">INACTIVE</span>}
        {product.sizes?.length > 0 && (
          <div className="flex gap-1 mt-2 flex-wrap">
            {product.sizes.map(s => (
              <button key={s} onClick={() => setSelectedSize(s)} className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${selectedSize === s ? 'bg-amber-100 border-amber-400 text-amber-800' : 'border-stone-200 text-stone-600 hover:border-amber-300'}`}>{s}</button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mt-3">
          <Button onClick={() => onAddToCart(product, selectedSize)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`add-to-cart-${product.id}`}>
            <ShoppingCart className="h-4 w-4 mr-1" /> Add to Cart
          </Button>
          {isAdmin && (
            <>
              <Button onClick={onEdit} variant="outline" size="sm"><Edit className="h-4 w-4" /></Button>
              <Button onClick={onDelete} variant="outline" size="sm" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const ORDER_STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-600',
};

function OrderCard({ order, isAdmin, onStatusChange }) {
  return (
    <Card className="border-stone-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ORDER_STATUS_COLORS[order.status] || 'bg-stone-100'}`}>
              {order.status?.toUpperCase()}
            </span>
            <span className="font-mono text-sm text-stone-500">#{order.id}</span>
          </div>
          <span className="text-lg font-bold text-amber-700">${order.total?.toFixed(2)}</span>
        </div>
        {isAdmin && <p className="text-xs text-stone-500 mb-2">{order.user_name} ({order.user_email})</p>}
        <div className="space-y-1">
          {(order.items || []).map((item, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-stone-700">{item.name} {item.size && `(${item.size})`} x{item.quantity}</span>
              <span className="text-stone-500">${(item.unit_price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-stone-400 mt-2">{order.created_at ? new Date(order.created_at).toLocaleString() : ''}</p>
        {isAdmin && onStatusChange && (
          <div className="flex gap-1 mt-2 flex-wrap">
            {['confirmed', 'shipped', 'delivered', 'cancelled'].filter(s => s !== order.status).map(s => (
              <Button key={s} onClick={() => onStatusChange(order.id, s)} variant="outline" size="sm" className="text-xs capitalize">{s}</Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ProductPhotoUploader({ productId, images, headers, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if ((images?.length || 0) + files.length > 3) {
      toast.error('Maximum 3 photos per product');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('photos', f));
      const res = await axios.post(`${API}/api/admin/merchandise/${productId}/photos`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      onUpdate(res.data.images);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (index) => {
    try {
      const res = await axios.delete(`${API}/api/admin/merchandise/${productId}/photos/${index}`, { headers });
      onUpdate(res.data.images);
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  return (
    <div className="space-y-2 mt-2">
      <div className="flex gap-2 flex-wrap">
        {(images || []).map((img, i) => (
          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-stone-200">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button onClick={() => handleDelete(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
              <X className="h-3 w-3 text-white" />
            </button>
            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-white text-[8px] text-center font-bold">MAIN</span>}
          </div>
        ))}
        {(images?.length || 0) < 3 && (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 hover:border-amber-400 flex flex-col items-center justify-center text-stone-400 hover:text-amber-600 transition-colors"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" /><span className="text-[9px] mt-0.5">Add Photo</span></>}
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
      <p className="text-xs text-stone-400">Up to 3 photos. First photo is the main display image.</p>
    </div>
  );
}
